import { createFileRoute } from "@tanstack/react-router";
import { Link as RouterLink } from "@tanstack/react-router";
import { ArrowRight, Link2, Mail, QrCode, UserPlus, Wallet, Lock, Signature } from "lucide-react";
import { FeaturePage } from "@/components/marketing/FeaturePage";
import { alternateLinkList } from "@/lib/i18n/locale";
import heroImg from "@/assets/sharing-hero-tools.webp";
import walletImg from "@/assets/sharing-wallet.webp";
import leadImg from "@/assets/sharing-lead-capture.webp";

const IMG: Record<string, string> = { hero: heroImg, wallet: walletImg, lead: leadImg };

export const Route = createFileRoute("/el/platform/sharing")({
  head: () => ({
    meta: [
      { title: "Εργαλεία κοινοποίησης και δικτύωσης — banana.cards" },
      { name: "description", content: "NFC, QR, σύνδεσμοι, κάρτες Wallet και υπογραφές email. Ένα προφίλ, κάθε κανάλι κοινοποίησης." },
      { property: "og:title", content: "Εργαλεία κοινοποίησης και δικτύωσης — banana.cards" },
      { property: "og:description", content: "NFC, QR, σύνδεσμοι, κάρτες Wallet και υπογραφές email. Ένα προφίλ, κάθε κανάλι κοινοποίησης." },
      { property: "og:image", content: IMG['hero'] },
      { property: "og:locale", content: "el_GR" },
    ],
    links: alternateLinkList("/platform/sharing", "el"),
  }),
  component: Page,
});

function Page() {
  return (
    <FeaturePage
      eyebrow="Εργαλεία κοινοποίησης και δικτύωσης"
      title="Μοιραστείτε ποιοι είστε,"
      italic="παντού."
      description="Ανταλλάξτε στοιχεία επικοινωνίας μέσω NFC, QR, συνδέσμων, καρτών Wallet και υπογραφών email. Ένα προφίλ, αμέτρητοι τρόποι κοινοποίησης — σε κάθε συσκευή."
      heroImage={IMG['hero']}
      metrics={[{ v: "9", l: "κανάλια κοινοποίησης" }, { v: "0", l: "εγκαταστάσεις εφαρμογών" }, { v: "100%", l: "συμβατότητα με κάθε πλατφόρμα" }]}
      bullets={[
        { icon: QrCode, title: "Δυναμικοί κωδικοί QR", desc: "Αλλάξτε τον προορισμό όποτε θέλετε, χωρίς επανεκτύπωση. Παρακολουθήστε σαρώσεις, συσκευές και τοποθεσίες." },
        { icon: Link2, title: "Σύντομοι σύνδεσμοι", desc: "Εξατομικευμένα σύντομα URL με αναλυτικά στοιχεία κλικ — ιδανικά για συνδέσμους στο bio και καμπάνιες." },
        { icon: Mail, title: "Υπογραφή email", desc: "Προσθέστε την κάρτα σας σε κάθε email με ένα απλό εργαλείο δημιουργίας υπογραφής." }
      ]}
      deepDives={[
        { eyebrow: "Wallet", title: "Προσθήκη στο Apple Wallet", italic: "και στο Google Wallet με ένα άγγιγμα.", description: "Οι παραλήπτες αποθηκεύουν την κάρτα σας στο ψηφιακό πορτοφόλι τους για άμεση πρόσβαση — χωρίς εφαρμογή και αναζήτηση στα μηνύματα.", image: IMG["wallet"], reverse: false, points: ["Κάρτα Apple Wallet με αυτόματες ενημερώσεις", "Κάρτα Google Wallet", "Ζωντανές ενημερώσεις όταν αλλάζετε το προφίλ σας", "Πρόσβαση από την οθόνη κλειδώματος"] },
        { eyebrow: "Εκδηλώσεις", title: "Νέοι υποψήφιοι πελάτες,", italic: "οργανωμένοι αμέσως.", description: "Χρησιμοποιήστε ξεχωριστούς κωδικούς QR για εκθέσεις, συνέδρια και συναντήσεις. Κάθε σάρωση περνά στο CRM σας με αυτόματη ετικέτα της εκδήλωσης.", image: IMG["lead"], reverse: true, points: ["Κωδικοί QR ανά εκδήλωση", "Αυτόματες ετικέτες στο CRM", "Προσαρμοσμένη φόρμα συλλογής υποψήφιων πελατών ανά εκδήλωση", "Εξαγωγή CSV και Zapier"] }
      ]}
      howItWorks={[
        { title: "Επιλέξτε τρόπο κοινοποίησης", desc: "Άγγιγμα NFC, QR, σύνδεσμος, υπογραφή ή Wallet." },
        { title: "Ο παραλήπτης ανοίγει το προφίλ", desc: "Το προφίλ ανοίγει αμέσως στο πρόγραμμα περιήγησής του." },
        { title: "Πατά αποθήκευση", desc: "Η κάρτα προστίθεται στις επαφές ή στο πορτοφόλι του." },
        { title: "Όλα μένουν συγχρονισμένα", desc: "Οι αλλαγές σας ενημερώνουν το αποθηκευμένο αντίγραφο." }
      ]}
      capabilities={["Απεριόριστοι κωδικοί QR", "Σύντομοι σύνδεσμοι με την εταιρική σας ταυτότητα", "Apple Wallet και Google Wallet", "Εργαλείο δημιουργίας υπογραφής email", "Παρακολούθηση ανά εκδήλωση", "Αναλυτικά στοιχεία ανά κανάλι", "Στοιχεία τοποθεσίας και συσκευής", "Διατήρηση παραμέτρων UTM"]}
      faq={[
        { q: "Με πόσους τρόπους μπορώ να μοιραστώ την κάρτα Banana;", a: "Τουλάχιστον επτά: άγγιγμα NFC (φυσική κάρτα σε τηλέφωνο), δυναμικός κωδικός QR (τυπωμένος, σε στιγμιότυπο ή διαφάνεια), σύντομος σύνδεσμος, κάρτα Apple Wallet ή Google Wallet, υπογραφή email, AirDrop ή το μενού κοινοποίησης της συσκευής, και άμεσο μήνυμα μέσω WhatsApp, iMessage, LinkedIn ή SMS. Κάθε κανάλι ανοίγει το ίδιο ζωντανό προφίλ." },
        { q: "Μπορώ να παρακολουθώ τις προβολές της κάρτας και την προέλευσή τους;", a: "Ναι — κάθε προβολή, άγγιγμα και σάρωση καταγράφεται με ώρα, συσκευή, πρόγραμμα περιήγησης, κατά προσέγγιση τοποθεσία και πηγή επισκεψιμότητας (QR, σύνδεσμος, Wallet ή υπογραφή). Τα αναλυτικά στοιχεία ομαδοποιούνται ανά κάρτα, μέλος ομάδας και καμπάνια, ώστε να βλέπετε τι αποδίδει πραγματικά." },
        { q: "Μπορώ να αλλάξω την κάρτα χωρίς επανεκτύπωση ή νέα αποστολή;", a: "Ναι — κάθε τρόπος κοινοποίησης οδηγεί στο ζωντανό προφίλ σας. Αλλάξτε τίτλο θέσης, φωτογραφία, συνδέσμους ή κουμπί δράσης μία φορά και κάθε QR, NFC, κάρτα Wallet και υπογραφή ενημερώνεται αμέσως. Οι δυναμικοί κωδικοί QR δεν λήγουν ποτέ." },
        { q: "Λειτουργεί η κοινοποίηση χωρίς σύνδεση του παραλήπτη στο διαδίκτυο;", a: "Το άγγιγμα NFC ή η σάρωση QR χρειάζεται σύντομη σύνδεση στο διαδίκτυο για τη φόρτωση του προφίλ. Μόλις όμως αποθηκευτούν τα στοιχεία σας στις επαφές ή η κάρτα στο Wallet, τηλέφωνο, email και δεδομένα vCard παραμένουν διαθέσιμα εκτός σύνδεσης." }
      ]}
      outro={{ title: "Μοιραστείτε μία φορά.", italic: "Μείνετε αξέχαστοι.", description: "Κάθε κανάλι, κάθε συσκευή — συνδεδεμένα με ένα προφίλ." }}
      afterFaq={<NetworkingToolsGrid />}
    />
  );
}

const TOOLS = [
  { title: "Φόρμα ανταλλαγής στοιχείων", desc: "Μετατρέψτε κάθε προβολή κάρτας σε νέο υποψήφιο πελάτη.", href: "/platform/networking/lead-exchange", icon: UserPlus },
  { title: "Email καλωσορίσματος", desc: "Στείλτε αυτόματα ένα καλωσόρισμα με την εταιρική σας ταυτότητα μόλις συνδεθεί κάποιος.", href: "/platform/networking/welcome-email", icon: Mail },
  { title: "Apple Wallet και Google Wallet", desc: "Αποθηκεύστε την κάρτα σας σε κάθε ψηφιακό πορτοφόλι, πάντα συγχρονισμένη.", href: "/platform/networking/wallet", icon: Wallet },
  { title: "Προστασία PIN", desc: "Κλειδώστε την κάρτα σας με τετραψήφιο PIN για ιδιωτική κοινοποίηση.", href: "/platform/networking/pin-protection", icon: Lock },
  { title: "Δυναμικός κωδικός QR", desc: "Εξατομικευμένοι κωδικοί QR ανά κάρτα, με παρακολούθηση και αλλαγή προορισμού.", href: "/platform/networking/qr-code", icon: QrCode },
  { title: "Υπογραφή email", desc: "Δημιουργήστε μια κομψή υπογραφή για Gmail, Outlook και Mail.", href: "/platform/networking/email-signature", icon: Signature },
];

function NetworkingToolsGrid() {
  return (
    <section className="bg-background">
      <div className="mx-auto max-w-7xl px-4 py-20 md:px-6">
        <div className="mb-10 max-w-2xl">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Εργαλεία δικτύωσης</p>
          <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
            Κάθε κανάλι κοινοποίησης, <em className="italic">στη διάθεσή σας.</em>
          </h2>
          <p className="mt-4 max-w-md text-base text-muted-foreground">
            Έξι εξειδικευμένα εργαλεία μετατρέπουν κάθε άγγιγμα σε ουσιαστική συζήτηση. Χρησιμοποιήστε ένα ή όλα.
          </p>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {TOOLS.map((t) => {
            const Icon = t.icon;
            return (
              <RouterLink
                key={t.href}
                to={t.href}
                aria-label={`${t.title} — μάθετε περισσότερα για αυτό το εργαλείο δικτύωσης`}
                className="group flex flex-col rounded-2xl bg-muted p-6 transition hover:-translate-y-0.5"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-zinc-50">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-5 text-lg font-bold">{t.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{t.desc}</p>
                <span className="mt-6 inline-flex items-center gap-1 text-sm font-bold underline-offset-4 group-hover:underline">
                  Μάθετε περισσότερα <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                </span>
              </RouterLink>
            );
          })}
        </div>
      </div>
    </section>
  );
}
